import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from './LoadingSpinner'
import { Button } from '@/components/ui/button'
import { roleService } from '../services/roleService'

export default function ProtectedRoute({ children, allowedRoles = [], requiredPermission = null }) {
  const { currentUser, role, userProfile, loading, signOut, profileError } = useAuth()
  const location = useLocation()
  const [roleWaitTimedOut, setRoleWaitTimedOut] = useState(false)

  const normalizedRole = (() => {
    const r = String(role || '').trim().toLowerCase()
    if (!r) return ''
    if (r === 'admin') return 'owner'
    return r
  })()

  const normalizedAllowedRoles = (allowedRoles || []).map((r) => {
    const v = String(r || '').trim().toLowerCase()
    if (v === 'admin') return 'owner'
    return v
  })

  // Brief grace period while profile hydrates — then stop spinning forever
  useEffect(() => {
    if (loading || !currentUser || normalizedRole) {
      setRoleWaitTimedOut(false)
      return
    }
    const t = setTimeout(() => setRoleWaitTimedOut(true), 8000)
    return () => clearTimeout(t)
  }, [loading, currentUser, normalizedRole])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Signed in but role still missing — wait briefly, then show recovery UI
  if (!normalizedRole && normalizedAllowedRoles.length > 0) {
    if (!roleWaitTimedOut && !profileError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      )
    }

    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 text-center space-y-4 shadow-sm">
          <h1 className="text-lg font-semibold text-foreground">Couldn’t load your account</h1>
          <p className="text-sm text-muted-foreground">
            {profileError ||
              'Your profile role could not be loaded. Sign out and try again, or contact support.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button type="button" variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
            <Button
              type="button"
              onClick={async () => {
                try {
                  await signOut()
                } finally {
                  window.location.assign('/login')
                }
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (normalizedAllowedRoles.length > 0 && !normalizedAllowedRoles.includes(normalizedRole)) {
    console.warn('[ProtectedRoute] access denied', {
      path: location.pathname,
      role,
      normalizedRole,
      allowedRoles,
      normalizedAllowedRoles,
      uid: currentUser?.uid,
    })
    if (normalizedRole === 'customer') {
      return <Navigate to="/customer" replace />
    }
    return <Navigate to="/" replace />
  }

  if (requiredPermission && !roleService.hasPermission(userProfile, requiredPermission)) {
    if (normalizedRole === 'customer') {
      return <Navigate to="/customer" replace />
    }
    return <Navigate to="/" replace />
  }

  return children
} 